import { twMerge } from "tailwind-merge";
import { TableConfiguration, TableData } from "../types";

type TableColumnConfig = {
    fieldKey: TableHeader;
    title: string;
    asHTML?: boolean;
    className?: string;
    valueGetter?: (data: TableData) => string | number | React.ReactNode;
};

type TableHeader =
    | "parameter"
    | "measured"
    | "predicted"
    | "precentOfPredicted"
    | "prePostPrecent"
    | "threshold"
    | "status";

function useTableColumnsConfig(
    configuration: TableConfiguration
): TableColumnConfig[] {
    const defaultConfigDict: Record<
        TableHeader,
        Omit<TableColumnConfig, "fieldKey">
    > = {
        parameter: {
            title: "Parameter",
            className: "w-24 xl:w-[130px] text-start",
            asHTML: true,
            valueGetter: (data: TableData) => {
                return data.parameterName;
            },
        },
        measured: {
            title: "Measured",
            className: "w-14 xl:w-20",
        },
        predicted: {
            title: "Predicted",
            className: "w-20 xl:w-[120px]",
        },
        precentOfPredicted: {
            title: "% of Predicted",
            className: "w-24 xl:w-[121px]",
        },
        prePostPrecent: {
            title: "Δ Pre-Post (%)",
            className: "w-24 xl:w-[109px]",
        },
        threshold: {
            title: "< Threshold (%)",
            className: "w-24 xl:w-[121px]",
        },
        status: {
            title: "Status",
            className:
                "flex items-center gap-2 w-28 xl:w-[160px] text-start pl-2",
            valueGetter: (data: TableData) => {
                return (
                    <>
                        <div
                            style={{
                                backgroundColor: data.statusColor,
                            }}
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                        />
                        <span className="line-clamp-1 py-0.5">
                            {data.status}
                        </span>
                    </>
                );
            },
        },
    };

    const configsByTableType: Record<
        TableConfiguration,
        Array<Partial<TableColumnConfig> & { fieldKey: TableHeader }>
    > = {
        [TableConfiguration.DEFAULT]: [
            { fieldKey: "parameter", className: "w-[140px] xl:w-[160px] 2xl:w-[200px]" },
            { fieldKey: "measured" },
            { fieldKey: "predicted" },
            { fieldKey: "precentOfPredicted" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.PEAK_VALUES]: [
            {
                fieldKey: "parameter",
                className: "w-[140px] xl:w-[180px] 2xl:w-[200px]",
            },
            { fieldKey: "measured" },
            { fieldKey: "predicted", className: "xl:w-24" },
            { fieldKey: "precentOfPredicted", className: "xl:w-[120px]" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.INTERPRETATION]: [
            {
                fieldKey: "parameter",
                className: "w-[140px] xl:w-[190px] 2xl:w-[200px]",
            },
            { fieldKey: "measured", className: "xl:w-[100px]" },
            { fieldKey: "predicted", className: "xl:w-[121px]" },
            { fieldKey: "precentOfPredicted" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.SLOPES]: [
            { fieldKey: "parameter", className: "xl:w-[195px]" },
            { fieldKey: "measured", className: "xl:w-[100px]" },
            { fieldKey: "predicted" },
            { fieldKey: "precentOfPredicted" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.RESPONSES]: [
            { fieldKey: "parameter", className: "w-32 xl:w-[180px]" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.PRE_SPIROMETRY]: [
            { fieldKey: "parameter", className: "w-32 xl:w-[195px]" },
            { fieldKey: "measured", className: "w-20 xl:w-[100px]" },
            { fieldKey: "predicted" },
            { fieldKey: "precentOfPredicted" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.POST_SPIROMETRY]: [
            { fieldKey: "parameter" },
            { fieldKey: "measured" },
            { fieldKey: "prePostPrecent" },
            { fieldKey: "threshold" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.DYNAMIC_LOOP]: [
            { fieldKey: "parameter", className: "w-32 xl:w-[204px]" },
            { fieldKey: "measured", className: "xl:w-[110px]" },
            {
                fieldKey: "threshold",
                className: "xl:w-[110px]",
                title: "< Threshold",
            },
            { fieldKey: "status" },
        ],
        [TableConfiguration.LAB_RESULTS]: [
            { fieldKey: "parameter", className: "w-36 xl:w-48" },
            { fieldKey: "measured", className: "w-24 xl:w-28" },
            { fieldKey: "predicted", className: "w-24 xl:w-28" },
            { fieldKey: "status" },
        ],
        [TableConfiguration.PERSON]: [
            { fieldKey: "parameter" },
            { fieldKey: "measured" },
        ],
    };

    const tableConfig = configsByTableType[configuration];
    const tableColumnsConfig = tableConfig.map((config) => {
        const defaultConfig = defaultConfigDict[config.fieldKey];

        return {
            ...defaultConfig,
            ...config,
            className: twMerge(defaultConfig.className, config.className),
        };
    });

    return tableColumnsConfig;
}

export default useTableColumnsConfig;
